// Typed AppState fixtures (SPEC §4 demo). Everything is annotated as AppState / shared types, so contract drift
// breaks `tsc`. Used by the mock backend and handy for screen development and tests.
import type {
  AppState, BuddyState, Card, CharId, Message, ProposedCard, RegisterRequest, StreakState, DayMark, FriendsState,
} from '@shared/types';
import { CHAR_IDS } from '@shared/types';
import { C } from '@shared/content/characters';
import { LINES } from '@shared/content/lines';
import { say } from '../lib/say';
import { CHAPTER, KEEPNAME } from '@shared/content/story';
import { obHello } from '@shared/content/lines';
import {
  addDays, buildRoute, chaptersOfStops, closenessOf, dateLabel, dayKey, fmtTime, isoWeekday, keepsakesOfStops, levelOf,
  shortDate, soundsOfStops, stageOfStops, weekStart, FREE_MESSAGES_PER_DAY, localMinutes,
} from '@shared/rules';

export const DEMO_TZ = 'America/Los_Angeles';
/** Day 0 = Sunday Nov 1 2026 (signup); day 9 = Tuesday Nov 10 2026, 9:12 AM local. */
export const DEMO_DAY0 = '2026-11-01';
export const DEMO_TODAY = '2026-11-10';
export const DEMO_NOW = '2026-11-10T17:12:00.000Z';
export const DEMO_INVITE = 'XM-7Q4K';
export const DEMO_EMAIL = 'you@email.com';
export const DEMO_PASSWORD = 'xiaoman';

/** SPEC §4 plan (same tasks for every character). */
export const PLAN: { id: string; at: number; title: string; min: number }[] = [
  { id: 'r', at: 570, title: 'Q3 report: write the next part', min: 25 },
  { id: 'd', at: 690, title: 'Call the dentist', min: 5 },
  { id: 'l', at: 840, title: 'Laundry: start one load', min: 10 },
  { id: 'w', at: 1110, title: 'Walk around the block', min: 15 },
];
/** Replaces 'r' when the report is split. */
export const SPLIT: { id: string; at: number; title: string; min: number }[] = [
  { id: 'r1', at: 570, title: 'Reread the last paragraph', min: 3 },
  { id: 'r2', at: 575, title: 'Write 3 bullet points', min: 7 },
  { id: 'r3', at: 585, title: 'Turn one bullet into sentences', min: 15 },
];
export const USER_DAY = 'Finish the Q3 report, call the dentist, do laundry. And I want to walk tonight.';
export const USER_THING = "The Q3 report. It's been sitting there for two weeks.";
export const PAST_CARDS = ['Q3 report: write the next part', 'Archive 10 emails', 'Laundry: start one load', 'Call the dentist',
  'Two lines of the cover letter', 'Walk around the block', 'One slide title', 'Dishes into the sink', 'Open the lease email',
  'Open the report. Write one sentence.'];
export const MEMORY = ["You're working on a Q3 report. Starting is the hard part.", 'Mornings start after coffee.',
  'You like short, plain cards.', 'Phone in another room helps.', 'Sundays are rest days.'];

export function mkCard(day: string, p: { id: string; at: number; title: string; min: number } & Partial<Card>, order = 0): Card {
  return {
    day, time: fmtTime(p.at), st: 'todo', source: 'plan', order, from: null, startedAt: null, doneAt: null, stuck: false,
    alt: null, movedTo: null, note: null, ...p,
  };
}
export const proposed = (p: { at: number; title: string; min: number }): ProposedCard => ({ time: fmtTime(p.at), at: p.at, title: p.title, min: p.min });

export function buddyState(ch: CharId, o: { stops: number; level: number; daysTogether: number; met?: boolean; newChapter?: number | null; memory?: string[] }): BuddyState {
  const route = buildRoute(ch, o.stops, o.level, {
    chapter: (n) => CHAPTER[ch][n - 1], placeName: C[ch].placeName, keepName: (i) => KEEPNAME[ch][i],
  });
  return {
    ch, met: o.met ?? true, daysTogether: o.daysTogether, closeness: closenessOf(o.daysTogether), stops: o.stops,
    stage: stageOfStops(o.stops), chaptersOpen: chaptersOfStops(o.stops), keepsakes: keepsakesOfStops(ch, o.stops),
    sounds: soundsOfStops(o.stops), route, chapterChoices: {}, newChapter: o.newChapter ?? null,
    memory: (o.memory || []).map((text, i) => ({ id: 'mem' + (i + 1), text, ts: '2026-11-0' + Math.min(9, i + 2) + 'T18:00:00.000Z' })),
    relationship: 'buddy',
  };
}

const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
export function streakState(today: string, count: number, litToday: boolean, marks: Record<string, DayMark> = {}): StreakState {
  const mon = weekStart(today);
  const week = WD.map((label, i) => {
    const day = addDays(mon, i);
    const mark: DayMark = marks[day] || (day === today ? (litToday ? 'lit' : 'today') : day < today ? 'lit' : 'future');
    return { day, label, mark };
  });
  return { count, litToday, best: Math.max(count, 9), freezeLeft: 1, week, safeNote: null };
}

const FRIENDS: FriendsState = {
  friends: [
    { id: 'f_jess', name: 'Jess', initial: 'J', color: '#4E6B54', together: 6, status: 'done', working: 'sunny' },
    { id: 'f_dev', name: 'Dev', initial: 'D', color: '#5A5084', together: 2, status: 'not-yet', working: null },
    { id: 'f_sam', name: 'Sam', initial: 'S', color: '#8A5A40', together: 0, status: 'new', working: null },
  ],
  club: { name: 'Thursday Club', done: 12, goal: 20 },
  sent: {},
};

let seq = 0;
const mid = (p: string) => p + '_' + (++seq).toString(36);
export function msg(ch: CharId, role: Message['role'], kind: Message['kind'], text: string, ts: string, data?: unknown): Message {
  return { id: mid('m'), ts, role, ch, kind, text, data };
}

export type DemoVariant = 'ask' | 'proposed' | 'planned';

/** Day 9 demo: Tuesday Nov 10, streak 9 (today not lit yet), 650 points = level 6 (150 of 160), four cards. */
export function demoState(ch: CharId = 'margo', variant: DemoVariant = 'planned'): AppState {
  const today = DEMO_TODAY;
  const points = 650;
  const level = levelOf(points);
  const stops = level.level - 1; // one stop per level-up since level 1
  const L0 = LINES[ch];
  // Lines with tokens are filled the way the server fills them (first card of the plan, its time).
  const L = { ...L0, propose: say(L0.propose, { n: C[ch].name, task: PLAN[0].title }), planned: say(L0.planned, { n: C[ch].name, time: fmtTime(PLAN[0].at) }) };
  const t = (min: number) => `2026-11-10T${String(17 + Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}:00.000Z`;
  const messages: Message[] = [msg(ch, 'buddy', 'text', L.morning, t(0))];
  const cards = variant === 'planned' ? PLAN.map((p, i) => mkCard(today, p, i)) : [];
  if (variant !== 'ask') {
    messages.push(msg(ch, 'user', 'text', USER_DAY, t(2)));
    messages.push(msg(ch, 'buddy', 'text', L.propose, t(3)));
    messages.push(msg(ch, 'buddy', 'plan', '', t(3), { cards: PLAN.map(proposed), status: variant === 'planned' ? 'accepted' : 'pending' }));
  }
  if (variant === 'planned') messages.push(msg(ch, 'buddy', 'planned', L.planned, t(4)));
  const buddies = Object.fromEntries(CHAR_IDS.map((id) => [id, id === ch
    ? { met: true, daysTogether: 9, stops, stage: stageOfStops(stops) }
    : { met: false, daysTogether: 0, stops: 0, stage: 1 }])) as AppState['buddies'];
  return {
    v: 1,
    now: DEMO_NOW,
    today,
    dateLabel: dateLabel(today),
    dayIndex: 9,
    user: {
      id: 'u_demo', email: DEMO_EMAIL, createdAt: '2026-11-01T22:40:00.000Z', tz: DEMO_TZ, tzAuto: true, invite: DEMO_INVITE,
      vip: false, planTime: '9:00 AM', checkin: 'steady', reminders: true,
      ob: { change: 'Get work done on time', stuck: 'Starting', tone: ['Straight to the point'], checkin: 'steady', planTime: '9:00 AM' },
      debugDays: 0,
    },
    ch,
    buddy: buddyState(ch, { stops, level: level.level, daysTogether: 9, newChapter: 3, memory: MEMORY }),
    buddies,
    streak: streakState(today, 9, false),
    points,
    level,
    plan: { day: today, status: variant === 'planned' ? 'planned' : variant === 'proposed' ? 'proposed' : 'asked', cards, perfectGiven: false },
    tomorrow: [],
    messages,
    hints: { back: false, offerToday: false, evening: false, firstNight: false },
    usage: { messagesToday: variant === 'ask' ? 0 : 1, freeLimit: FREE_MESSAGES_PER_DAY, costTotalUsd: 1.84, costTodayUsd: 0.02 },
    week: {
      start: weekStart(today), end: addDays(weekStart(today), 6),
      label: `${shortDate(weekStart(today))} – ${shortDate(addDays(weekStart(today), 6))}`,
      ready: false, lit: 1, cards: 3, onTime: 1, onTimePrev: 1, highlight: null, answer: null, reply: null, chest: 'locked',
    },
    collection: {
      cardsDone: 29,
      recent: PAST_CARDS.map((title, i) => ({ id: 'past' + i, title, day: addDays(today, -1 - i), doneAt: `${addDays(today, -1 - i)}T17:4${i % 10}:00.000Z` })),
    },
    friends: FRIENDS,
    reward: null,
    tomorrowPlanTime: '9:00 AM',
  };
}

/** State right after O11 (register): day 0, first card done (lights day 0), +10 points. */
export function dayZeroState(req: RegisterRequest, nowMs: number = Date.now()): AppState {
  const tz = req.tz || DEMO_TZ;
  const today = dayKey(nowMs, tz);
  const ch = req.ch;
  const L = LINES[ch];
  const nowIso = new Date(nowMs).toISOString();
  const done = !!req.first.done;
  const points = done ? 10 : 0;
  const level = levelOf(points);
  const firstCard: Card = mkCard(today, {
    id: 'c_first', at: localMinutes(nowMs, tz), title: req.first.card, min: Math.max(1, req.first.minutes || 2),
    st: done ? 'done' : 'todo', source: 'onboarding', doneAt: done ? (req.first.doneAt || nowIso) : null, note: req.first.note || null,
  });
  const tomorrow: Card[] = [];
  const later = req.first.tomorrow;
  const tonight = req.first.tomorrowDay === 'today';
  const todayCards: Card[] = [firstCard];
  if (later && !tonight) tomorrow.push(mkCard(addDays(today, 1), { id: 'c_later', at: later.at, title: later.title, min: later.min, source: 'onboarding' }));
  if (later && tonight) todayCards.push(mkCard(today, { id: 'c_later', at: later.at, title: later.title, min: later.min, source: 'onboarding' }, 1));
  const messages: Message[] = [
    msg(ch, 'buddy', 'text', obHello(ch, req.ob.stuck), nowIso),   // the O8 opening line they saw (follows O3)
    msg(ch, 'user', 'text', req.first.said || req.first.thing, nowIso),
    msg(ch, 'buddy', 'text', req.first.reply || L.firstReply, nowIso),
  ];
  if (later && !tonight) messages.push(msg(ch, 'buddy', 'tomorrow', '', nowIso, { card: later }));
  messages.push(msg(ch, 'buddy', 'text', L.dayZeroNight, nowIso));
  const buddies = Object.fromEntries(CHAR_IDS.map((id) => [id, { met: id === ch, daysTogether: id === ch && done ? 1 : 0, stops: 0, stage: 1 }])) as AppState['buddies'];
  return {
    v: 1, now: nowIso, today, dateLabel: dateLabel(today), dayIndex: 0,
    user: {
      id: 'u_' + Math.random().toString(36).slice(2, 10), email: req.email, createdAt: nowIso, tz, tzAuto: true, invite: req.invite,
      vip: false, planTime: req.ob.planTime, checkin: req.ob.checkin, reminders: req.reminders !== false, ob: req.ob, debugDays: 0,
    },
    ch,
    buddy: buddyState(ch, { stops: 0, level: level.level, daysTogether: done ? 1 : 0 }),
    buddies,
    streak: streakState(today, done ? 1 : 0, done, Object.fromEntries(
      Array.from({ length: isoWeekday(today) - 1 }, (_, i) => [addDays(weekStart(today), i), 'missed' as DayMark]),
    )),
    points, level,
    plan: { day: today, status: 'planned', cards: todayCards, perfectGiven: false },
    tomorrow,
    messages,
    hints: { back: false, offerToday: false, evening: false, firstNight: true },
    usage: { messagesToday: 1, freeLimit: FREE_MESSAGES_PER_DAY, costTotalUsd: 0.01, costTodayUsd: 0.01 },
    week: null,
    collection: { cardsDone: done ? 1 : 0, recent: done ? [{ id: firstCard.id, title: firstCard.title, day: today, doneAt: firstCard.doneAt || nowIso }] : [] },
    friends: FRIENDS,
    reward: null,
    tomorrowPlanTime: req.ob.planTime,
  };
}
